/* eslint-disable no-restricted-imports */
import { AnyObject, Decorator, FormApi, getIn, SubmissionErrors, ValidationErrors } from 'final-form'

/** Foca o primeiro campo com erro após tentativa de submit. */

interface FocusableInput {
  name: string
  focus(): void
}

const isFocusable = (element: Element): element is HTMLElement & { name: string } => {
  const candidate = element as HTMLInputElement
  return typeof candidate.focus === 'function' && !!candidate.name && !candidate.disabled && candidate.type !== 'hidden'
}

const getAllInputs = (): FocusableInput[] =>
  Array.from(document.forms).flatMap((form) =>
    Array.from(form.elements)
      .filter(isFocusable)
      .map((element) => ({ name: element.name, focus: () => element.focus() }))
  )

const findFirstErroredInput = (errors: AnyObject): FocusableInput | undefined =>
  getAllInputs().find((input) => getIn(errors, input.name))

export function createFocusOnErrorDecorator<FormValues = any>(): Decorator<FormValues> {
  return (form: FormApi<FormValues>) => {
    let errors: ValidationErrors
    let submitErrors: SubmissionErrors

    const unsubscribe = form.subscribe(
      (state) => {
        errors = state.errors
        submitErrors = state.submitErrors
      },
      { errors: true, submitErrors: true }
    )

    const focusOnFirstError = () => {
      const target = errors && Object.keys(errors).length ? errors : submitErrors
      if (target && Object.keys(target).length) findFirstErroredInput(target)?.focus()
    }

    const originalSubmit = form.submit
    form.submit = () => {
      const result = originalSubmit.call(form)

      if (result && typeof result.then === 'function') {
        result.then(focusOnFirstError).catch(() => undefined)
      } else {
        focusOnFirstError()
      }

      return result
    }

    return () => {
      unsubscribe()
      form.submit = originalSubmit
    }
  }
}
